import { startGovernorCommandCenterLiveBridge } from '@/governor/live/GovernorCommandCenterLiveBridge';
import {
  certifyCallsEndToEndLifecycle,
} from '@/governor/calls/CallsEndToEndLifecycleCertificationEngine';
import GovernorRepairSuggestions from '@/components/governor/GovernorRepairSuggestions';
import GovernorLiveTimeline from '@/components/governor/GovernorLiveTimeline';
import { runUnifiedGovernorRuntime } from '@/governor/core/GovernorRuntime';
import {
  subscribeCallsRuntimeObservations,
  type CallsRuntimeObservation,
} from '@/governor/calls/CallsRuntimeObservationBridge';
import { useState, useEffect } from 'react';
import SystemWorkspaceDirectory from '@/components/governor/SystemWorkspaceDirectory';
import LegacyCommandCenterMigrationInventory from '@/components/governor/LegacyCommandCenterMigrationInventory';
import LegacyCommandCenterCompletionCertification from '@/components/governor/LegacyCommandCenterCompletionCertification';
import { supabase } from '@/lib/supabase';

import { projectCallsRuntimeDecision } from '../governor/calls-runtime/CallsRuntimeDecisionProjectionEngine';
import { buildCallsRuntimeIncidentState } from '../governor/calls-runtime/CallsRuntimeIncidentEngine';
import { buildCallsIncidentResponsePlans } from '../governor/calls-runtime/CallsIncidentResponsePlanningEngine';
import { evaluateCallsRepairAuthorities } from '../governor/calls-runtime/CallsRepairAuthorityGateEngine';
import { buildCallsEndToEndPipelines } from '../governor/calls-runtime/CallsEndToEndPipelineProjectionEngine';
import { buildCallsGovernorExecutionRequest } from '../governor/calls-runtime/CallsGovernorExecutionContractBridge';
import {
  projectSecurityAuthorityEvidence,
} from '../governor/security/SecurityAuthorityEvidenceProjectionEngine';
import {
  evaluateSecurityGovernorCompletionGate,
} from '../governor/security/SecurityGovernorCompletionGateEngine';
import {
  projectSponsoredSeatRuntimeEvidence,
} from '../governor/membership/SponsoredSeatRuntimeEvidenceProjectionEngine';
import {
  evaluateSponsoredSeatCompletionGate,
} from '../governor/membership/SponsoredSeatCompletionGateEngine';
import {
  projectUnsponsoredFamilyRuntimeEvidence,
} from '../governor/membership/UnsponsoredFamilyRuntimeEvidenceProjectionEngine';
import {
  evaluateUnsponsoredFamilyCompletionGate,
} from '../governor/membership/UnsponsoredFamilyCompletionGateEngine';
import {
  projectMembershipCounterRuntimeEvidence,
} from '../governor/membership/MembershipCounterRuntimeEvidenceProjectionEngine';
import {
  evaluateMembershipCounterCompletionGate,
} from '../governor/membership/MembershipCounterCompletionGateEngine';
import {
  evaluateCanonicalRouteRuntimeCompletionGate,
} from '../governor/routing/CanonicalRouteRuntimeCompletionGateEngine';
function SafePanel({
  title,
  children,
  fallback,
}: {
  title: string;
  children: any;
  fallback?: any;
}) {
  try {
    return <>{children}</>;
  } catch (e: any) {
    return (
      <section className="rounded-[2rem] bg-white p-6 mb-6 shadow-sm border border-red-200">
        <h2 className="text-3xl mb-3 text-red-900">{title} failed</h2>
        <p className="text-slate-700 mb-3">
          This panel crashed, but the Command Center stayed online.
        </p>
        <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-900 break-words">
          {e?.message || String(e)}
        </div>
        {fallback || null}
      </section>
    );
  }
}


function arr(v:any): any[] {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'object') return Object.values(v).flat().filter(Boolean);
  return [v];
}

function has(v:any) {
  if (!v) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}

function pick(o:any, keys:string[]) {
  for (const k of keys) {
    const direct = o?.[k];
    if (has(direct)) return direct;

    const nested = k.split('.').reduce((acc:any, part:string) => acc?.[part], o);
    if (has(nested)) return nested;
  }
  return null;
}

function Panel({ title, value }: { title:string; value:any }) {
  try {
  const rows = arr(value);
  const summary = value && !Array.isArray(value) && typeof value === 'object' ? value : null;

  return (
    <section className="rounded-[2rem] bg-white p-6 mb-6 shadow-sm">
      <h2 className="text-3xl mb-5">{title}</h2>

      {summary && (
        <div className="rounded-2xl bg-[#f3eefc] p-4 mb-4">
          {Object.entries(summary)
            .filter(([,v]) => typeof v !== 'object')
            .map(([k,v]) => <p key={k}><b>{k}:</b> {String(v)}</p>)}
        </div>
      )}

      {rows.length ? rows.slice(0, 12).map((item:any, i:number) => (
        <div key={i} className="rounded-2xl bg-[#eafff1] p-4 mb-3 border border-[#c9f3d8]">
          {typeof item === 'object' ? (
            <>
              <p className="font-bold text-lg">
                {item.title || item.goal || item.strategy || item.issue || item.subsystem || `Item ${i + 1}`}
              </p>
              {Object.entries(item).slice(0, 12).map(([k,v]) => (
                typeof v === 'object' ? null : <p key={k}><b>{k}:</b> {String(v)}</p>
              ))}
            </>
          ) : <p>{String(item)}</p>}
        </div>
      )) : <p className="text-gray-500">Waiting for Governor event.

Reason:
No runtime evidence has been received for this subsystem yet.

Expected trigger:
A real action (tree edit, message, call, login, repair, deploy, etc.) must reach the Governor.

Next check:
If this panel never changes after exercising its feature, the Governor should inspect that subsystem's event pipeline.</p>}
    </section>
  );
  } catch (e:any) {
    return (
      <section className="rounded-[2rem] bg-white p-6 mb-6 shadow-sm border border-red-200">
        <h2 className="text-3xl mb-3 text-red-900">{title} failed</h2>
        <p className="text-slate-700 mb-3">This panel failed safely and did not collapse the Command Center.</p>
        <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-900 break-words">
          {e?.message || String(e)}
        </div>
      </section>
    );
  }
}

export default function GovernorCommandCenter() {
  // RC-006: Manual run is the only Command Center trigger.
  // Runtime bridge may listen, but it must not auto-run the full Governor.

  const [runtime, setRuntime] = useState<any>(null);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState('');
  const [
    treeStructuralRepairPlan,
    setTreeStructuralRepairPlan,
  ] = useState<any>(null);

  const [sponsoredPretestRunning, setSponsoredPretestRunning] =
    useState(false);
  const [sponsoredPretestReport, setSponsoredPretestReport] =
    useState<any>(null);
  const [sponsoredPretestError, setSponsoredPretestError] =
    useState<string | null>(null);

  useEffect(() => {
    const hydrateTreeStructuralRepairPlan = (
      event?: Event,
    ) => {
      const eventProjection =
        (
          event as CustomEvent<any> | undefined
        )?.detail ?? null;

      const windowProjection =
        (
          window as unknown as Record<
            string,
            unknown
          >
        ).__kcTreeStructuralRepairPlan ?? null;

      setTreeStructuralRepairPlan(
        eventProjection ?? windowProjection,
      );
    };

    hydrateTreeStructuralRepairPlan();

    window.addEventListener(
      'kc:tree-structural-repair-plan-updated',
      hydrateTreeStructuralRepairPlan,
    );

    return () => {
      window.removeEventListener(
        'kc:tree-structural-repair-plan-updated',
        hydrateTreeStructuralRepairPlan,
      );
    };
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(
        'kc:sponsored-invite-pretest',
      );

      if (stored) {
        setSponsoredPretestReport(
          JSON.parse(stored),
        );
      }
    } catch {
      localStorage.removeItem(
        'kc:sponsored-invite-pretest',
      );
    }

    const unsubscribeLiveBridge = startGovernorCommandCenterLiveBridge((snapshot) => {
      setRuntime((previousRuntime: any) => {
        const currentRuntime = previousRuntime || {};
        const currentScoreboard = currentRuntime.executiveScoreboard || {};

        const persistedCallsObservations =
          snapshot.actions
            .filter(
              (action: any) =>
                action?.type ===
                'governor_calls_runtime_event',
            )
            .slice(0, 12)

            .map((action: any) =>
              projectCallsRuntimeDecision(action),
            );

        const callsRuntimeIncidentState =
          buildCallsRuntimeIncidentState(
            persistedCallsObservations,
          );

        const callsIncidentResponsePlans =
          buildCallsIncidentResponsePlans(
            callsRuntimeIncidentState.active,
          );

        const callsRepairAuthorityDecisions =
          evaluateCallsRepairAuthorities(
            callsIncidentResponsePlans,
            [],
            snapshot.lastUpdated,
          );

        const callsGovernorExecutionRequests =
          callsIncidentResponsePlans.map(
            plan =>
              buildCallsGovernorExecutionRequest(
                plan,
                callsRepairAuthorityDecisions.find(
                  decision =>
                    decision.planId === plan.id &&
                    decision.incidentId ===
                      plan.incidentId,
                ),
                snapshot.lastUpdated,
              ),
          );

        const callsEndToEndPipelines =
          buildCallsEndToEndPipelines({
            observations:
              persistedCallsObservations,
            incidents:
              callsRuntimeIncidentState.active,
            responsePlans:
              callsIncidentResponsePlans,
            authorityDecisions:
              callsRepairAuthorityDecisions,
          });

        const latestCallsObservation =
          persistedCallsObservations[0] ?? null;

        return {
          ...currentRuntime,
          liveActions: snapshot.actions,
          runtimeSignals: snapshot.actions,
          callsRuntimeObservations:
            persistedCallsObservations,
          callsRuntimeObservationSummary: {
            observationCount:
              persistedCallsObservations.length,
            latestOwner:
              latestCallsObservation?.owner ??
              'Waiting',
            latestOperation:
              latestCallsObservation?.operation ??
              'Waiting',
            mode:
              latestCallsObservation?.mode ??
              'observe',
            certificationDecision:
              latestCallsObservation
                ?.certificationDecision ??
              'unverified',
            runtimeAllowed:
              latestCallsObservation
                ?.runtimeAllowed ??
              false,
            sharedEvidencePublished:
              latestCallsObservation
                ?.sharedEvidencePublished ??
              false,
            executiveRuntimeRecorded:
              latestCallsObservation
                ?.executiveRuntimeRecorded ??
              false,
            latestStage:
              latestCallsObservation?.stage ??
              'Waiting',
            latestStatus:
              latestCallsObservation?.status ??
              'Waiting',
            latestSource:
              latestCallsObservation?.source ??
              'Waiting',
            latestReason:
              latestCallsObservation?.reasonCode ??
              'Waiting',
            activeCallsIncidents:
              callsRuntimeIncidentState.active,
            resolvedCallsIncidents:
              callsRuntimeIncidentState.resolved,
            activeCallsIncidentCount:
              callsRuntimeIncidentState.active.length,
            resolvedCallsIncidentCount:
              callsRuntimeIncidentState.resolved.length,
            latestCallsIncident:
              callsRuntimeIncidentState.active[0] ??
              callsRuntimeIncidentState.resolved[0] ??
              null,
            callsIncidentResponsePlans,
            callsIncidentResponsePlanCount:
              callsIncidentResponsePlans.length,
            latestCallsIncidentResponsePlan:
              callsIncidentResponsePlans[0] ??
              null,
            callsResponsePlansAwaitingAuthority:
              callsIncidentResponsePlans.filter(
                plan =>
                  plan.status ===
                  'awaiting-authority',
              ).length,
            callsResponsePlansRetryEligible:
              callsIncidentResponsePlans.filter(
                plan =>
                  plan.retryEligible,
              ).length,
            callsRepairAuthorityDecisions,
            callsRepairAuthorityDecisionCount:
              callsRepairAuthorityDecisions.length,
            callsRepairAuthorityApprovedCount:
              callsRepairAuthorityDecisions.filter(
                decision =>
                  decision.decision === 'approved',
              ).length,
            callsRepairAuthorityWithheldCount:
              callsRepairAuthorityDecisions.filter(
                decision =>
                  decision.decision === 'withheld',
              ).length,
            callsRepairAuthorityDeniedCount:
              callsRepairAuthorityDecisions.filter(
                decision =>
                  decision.decision === 'denied',
              ).length,
            latestCallsRepairAuthorityDecision:
              callsRepairAuthorityDecisions[0] ??
              null,
            callsEndToEndPipelines,
            callsEndToEndPipelineCount:
              callsEndToEndPipelines.length,
            blockedCallsPipelineCount:
              callsEndToEndPipelines.filter(
                pipeline =>
                  pipeline.blocked,
              ).length,
            approvedCallsPipelineCount:
              callsEndToEndPipelines.filter(
                pipeline =>
                  pipeline.authorityApproved,
              ).length,
            latestCallsEndToEndPipeline:
              callsEndToEndPipelines[0] ??
              null,
            latestCallsFirstIncompleteStage:
              callsEndToEndPipelines[0]
                ?.firstIncompleteStage ??
              null,
            latestCallsNextRequiredGovernor:
              callsEndToEndPipelines[0]
                ?.nextRequiredGovernor ??
              null,
            callsGovernorExecutionRequests,
            callsGovernorExecutionRequestCount:
              callsGovernorExecutionRequests.length,
            callsGovernorExecutionReadyCount:
              callsGovernorExecutionRequests.filter(
                request =>
                  request.status === 'ready' &&
                  request.executionAllowed,
              ).length,
            callsGovernorExecutionBlockedCount:
              callsGovernorExecutionRequests.filter(
                request =>
                  request.status === 'blocked' ||
                  !request.executionAllowed,
              ).length,
            latestCallsGovernorExecutionRequest:
              callsGovernorExecutionRequests[0] ??
              null,
            latestCallsExecutionIdempotencyKey:
              callsGovernorExecutionRequests[0]
                ?.idempotencyKey ??
              null,
            latestCallsExecutionContractReason:
              callsGovernorExecutionRequests[0]
                ?.reason ??
              null,
            latestObservedAt:
              latestCallsObservation
                ?.observedAt ??
              'Waiting',
          },
          executiveScoreboard: {
            ...currentScoreboard,
            liveEvents: snapshot.actions.length,
            callsRuntimeObservations:
              persistedCallsObservations.length,
            lastUpdated: snapshot.lastUpdated,
            lastEvent:
              snapshot.actions[0] ?? null,
          },
        };
      });
    });

    const unsubscribeCallsObservations =
      subscribeCallsRuntimeObservations(
        (observations: readonly CallsRuntimeObservation[]) => {
          const latest =
            observations.length > 0
              ? observations[observations.length - 1]
              : null;

          const projection = observations
            .slice(-12)
            .reverse()
            .map((observation) => ({
              title: observation.owner,
              owner: observation.owner,
              operation: observation.operation,
              mode: observation.mode,
              certificationDecision:
                observation.certificationDecision,
              certificationAllowed:
                observation.certificationAllowed,
              runtimeAllowed: observation.runtimeAllowed,
              reasonCode: observation.reasonCode,
              sharedEvidencePublished:
                observation.sharedEvidenceId !== null,
              executiveRuntimeRecorded:
                observation.runtimeEvidenceRecorded,
              observedAt: observation.observedAt,
            }));

          setRuntime((previousRuntime: any) => {
            const currentRuntime = previousRuntime || {};
            const currentScoreboard =
              currentRuntime.executiveScoreboard || {};

            const existingCallsProjection =
              Array.isArray(
                currentRuntime.callsRuntimeObservations,
              )
                ? currentRuntime.callsRuntimeObservations
                : [];

            /*
             * The persistent Governor live-action path is authoritative.
             * The in-memory observation bridge may start empty and must
             * never erase real call lifecycle evidence already hydrated
             * from localStorage.
             */
            if (existingCallsProjection.length > 0) {
              return {
                ...currentRuntime,
                executiveScoreboard: {
                  ...currentScoreboard,
                  callsRuntimeBridgeObservations:
                    observations.length,
                  lastCallsRuntimeObservation:
                    latest?.observedAt ?? null,
                },
              };
            }

            return {
              ...currentRuntime,
              callsRuntimeObservations: projection,
              callsRuntimeObservationSummary: {
                observationCount: observations.length,
                latestOwner: latest?.owner ?? 'Waiting',
                latestOperation:
                  latest?.operation ?? 'Waiting',
                mode: latest?.mode ?? 'observe',
                certificationDecision:
                  latest?.certificationDecision ?? 'unverified',
                runtimeAllowed:
                  latest?.runtimeAllowed ?? false,
                sharedEvidencePublished:
                  latest?.sharedEvidenceId !== null &&
                  latest?.sharedEvidenceId !== undefined,
                executiveRuntimeRecorded:
                  latest?.runtimeEvidenceRecorded ?? false,
                latestObservedAt:
                  latest?.observedAt ?? 'Waiting',
              },
              executiveScoreboard: {
                ...currentScoreboard,
                callsRuntimeObservations:
                  observations.length,
                lastCallsRuntimeObservation:
                  latest?.observedAt ?? null,
              },
            };
          });
        },
      );

    return () => {
      if (typeof unsubscribeLiveBridge === "function") {
        unsubscribeLiveBridge();
      }

      unsubscribeCallsObservations();
    };
  }, []);



  async function runSponsoredInvitePretest() {
    setSponsoredPretestRunning(true);
    setSponsoredPretestError(null);

    try {
      const { data, error } = await supabase.rpc(
        'simulate_sponsored_invite_e2e',
      );

      if (error) {
        throw error;
      }

      if (
        !data ||
        data.status !== 'PASS' ||
        data.scenario !==
          'SPONSORED_INVITE_E2E_NO_SEND' ||
        data.cleanup_complete !== true ||
        data.seat_count_unchanged !== true ||
        data.external_delivery_attempted !== false
      ) {
        throw new Error(
          'Sponsored invite pretest returned an invalid result.',
        );
      }

      const report = {
        ...data,
        certified_at:
          new Date().toISOString(),
        expires_at:
          new Date(
            Date.now() + 15 * 60 * 1000,
          ).toISOString(),
      };

      setSponsoredPretestReport(report);

      localStorage.setItem(
        'kc:sponsored-invite-pretest',
        JSON.stringify(report),
      );

      setRuntime((previousRuntime: any) => ({
        ...(previousRuntime || {}),
        sponsoredInvitePretest: report,
      }));
    } catch (error: any) {
      const message =
        error?.message ||
        'Sponsored invite pretest failed.';

      setSponsoredPretestError(message);
      setSponsoredPretestReport(null);

      localStorage.removeItem(
        'kc:sponsored-invite-pretest',
      );
    } finally {
      setSponsoredPretestRunning(false);
    }
  }

  async function runFull() {
    setRunning(true);
    try {
      const mod = await import('@/governor/core/GovernorRuntime');
      const result:any = await
        mod.runUnifiedGovernorRuntime({
          source: 'ultimate-kinscircle-command-center',
          command: 'Ultimate Command Center Full Runtime'
        });
      const nextRuntime = result || {};

      setRuntime((previousRuntime: any) => ({
        ...nextRuntime,
        liveActions:
          previousRuntime?.liveActions ?? [],
        runtimeSignals:
          previousRuntime?.runtimeSignals ?? [],
        callsRuntimeObservations:
          previousRuntime
            ?.callsRuntimeObservations ?? [],
        callsRuntimeObservationSummary:
          previousRuntime
            ?.callsRuntimeObservationSummary ??
          null,
        executiveScoreboard: {
          ...(nextRuntime?.executiveScoreboard ??
            {}),
          ...(previousRuntime
            ?.executiveScoreboard ?? {}),
        },
      }));

      try {
        const event = {
          type: 'governor.runtime.run',
          source: 'GovernorCommandCenter',
          timestamp: new Date().toISOString(),
          domains: Object.keys(nextRuntime || {}),
          summary: {
            hasRuntime: !!nextRuntime,
            keys: Object.keys(nextRuntime || {}).length,
          },
        };

        const key = 'kinscircle_governor_runtime_events';
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        const nextEvents = [event, ...(Array.isArray(existing) ? existing : [])].slice(0, 50);

        localStorage.setItem(key, JSON.stringify(nextEvents));
        window.dispatchEvent(new CustomEvent('kinscircle:governor-runtime-event', { detail: event }));
      } catch {
        // Never let event capture collapse the Command Center.
      }
      setLastRun(new Date().toLocaleString());
    } catch (e:any) {
      setRuntime({ ok:false, error:e?.message || String(e) });
      setLastRun(new Date().toLocaleString());
    } finally {
      setRunning(false);
    }
  }

  const findings = arr(pick(runtime, ['findings', 'report']));
  const repairQueue = arr(pick(runtime, ['repairQueue', 'autoRepairQueue', 'repairs']));

  const liveActions = Array.isArray((runtime as any)?.liveActions)
    ? (runtime as any).liveActions
    : Array.isArray((runtime as any)?.runtimeSignals)
      ? (runtime as any).runtimeSignals
      : [];

  const liveDomainActions = (domain: string) =>
    liveActions.filter((action: any) => {
      try {
        const normalizedDomain =
          String(action?.detail?.domain || '')
            .trim()
            .toLowerCase();

        if (normalizedDomain) {
          return (
            normalizedDomain === domain.toLowerCase() ||
            (
              domain.toLowerCase() === 'call' &&
              normalizedDomain === 'calls'
            )
          );
        }

        return JSON.stringify(action || {})
          .toLowerCase()
          .includes(domain.toLowerCase());
      } catch {
        return false;
      }
    });

  const securityAuthorityEvidence =
    projectSecurityAuthorityEvidence();

  const securityGovernorCompletionGate =
    evaluateSecurityGovernorCompletionGate();


  const sponsoredSeatRuntimeEvidence =
    projectSponsoredSeatRuntimeEvidence();


  const sponsoredSeatCompletionGate =
    evaluateSponsoredSeatCompletionGate();


  const unsponsoredFamilyRuntimeEvidence =
    projectUnsponsoredFamilyRuntimeEvidence();


  const unsponsoredFamilyCompletionGate =
    evaluateUnsponsoredFamilyCompletionGate();


  const membershipCounterRuntimeEvidence =
    projectMembershipCounterRuntimeEvidence();


  const membershipCounterCompletionGate =
    evaluateMembershipCounterCompletionGate();


  const canonicalRouteRuntimeCompletionGate =
    evaluateCanonicalRouteRuntimeCompletionGate();

  const runtimeSignals = [
    ['Autonomous Governance', pick(runtime, ['autonomousRepairGovernance', 'repairGovernance', 'governanceDecisions'])],
    ['Autonomous Operations', pick(runtime, ['autonomousOperationsOutput', 'autonomousOperations', 'operations', 'executedOperations'])],
    ['Self-Healing', pick(runtime, ['selfHealing', 'selfHealingPlan', 'autonomousRepairRunner'])],
    ['Repair Routing', pick(runtime, ['repairRoutes', 'repairRouting', 'routes'])],
    ['Auto-Repair Queue', pick(runtime, ['autoRepairQueueOutput', 'autoRepairQueue', 'repairQueue', 'repairs']) || repairQueue],
    ['Verification Loop', pick(runtime, ['verificationLoops', 'verificationResults', 'verificationLoop'])],
    ['Learning', pick(runtime, ['learningInsights', 'learning', 'memoryInsights'])],
    ['Mission', pick(runtime, ['missions', 'mission'])],
    ['Goals', pick(runtime, ['goals', 'goalSummary'])],
    ['Strategy', pick(runtime, ['strategies', 'strategySummary'])],
    ['Priority', pick(runtime, ['prioritySummary', 'priorities'])],
    ['Impact', pick(runtime, ['impactReports', 'impact'])],
    ['Dashboard', pick(runtime, ['dashboardView', 'dashboard'])],
    ['Findings', findings],
  ];

  const activeSignals = runtime ? runtimeSignals.filter(([,v]) => has(v)).length : 0;
  const truthScore = runtime ? Math.max(1, Math.min(100, Math.round((activeSignals / runtimeSignals.length) * 100))) : 0;

  const sponsoredPretestFresh =
    sponsoredPretestReport?.status === 'PASS' &&
    Boolean(sponsoredPretestReport?.expires_at) &&
    new Date(
      sponsoredPretestReport.expires_at,
    ).getTime() > Date.now();

  const sponsoredInviteReleaseGate = {
    status: sponsoredPretestFresh
      ? 'PASS'
      : 'BLOCKED',
    certification_allowed:
      sponsoredPretestFresh,
    required_scenario:
      'SPONSORED_INVITE_E2E_NO_SEND',
    fresh_for_minutes: 15,
    certified_at:
      sponsoredPretestReport?.certified_at ??
      null,
    expires_at:
      sponsoredPretestReport?.expires_at ??
      null,
    external_delivery_attempted:
      sponsoredPretestReport
        ?.external_delivery_attempted ??
      null,
    reason: sponsoredPretestFresh
      ? 'A fresh no-send end-to-end pretest passed.'
      : sponsoredPretestError
        ? `Pretest failed: ${sponsoredPretestError}`
        : 'Run a fresh sponsored invite pretest before human testing or release certification.',
  };

  return (
    <main className="min-h-screen bg-[#13051f] text-[#21112f] px-4 py-6">
      <section className="rounded-[2rem] bg-gradient-to-br from-[#31105d] via-[#6d2ed8] to-[#9b5cff] text-white p-7 shadow-2xl">
        <p className="tracking-[0.25em] text-xs opacity-80">KINSCIRCLE SUPER BRAIN</p>
        <h1 className="text-4xl mt-4 leading-tight">Ultimate KinsCircle Command Center</h1>
        <p className="mt-5 text-lg leading-relaxed opacity-95">
          Executive control for Governor runtime, autonomous repair, verification, memory,
          known issues, tree intelligence, calls, messaging, Supabase, Netlify, Android,
          and live operational truth.
        </p>
        <button onClick={runFull} className="mt-7 rounded-2xl bg-white text-[#241036] font-bold px-6 py-4 shadow-lg w-full">
          {running ? 'Running Full Governor...' : 'Run Ultimate Command Center'}
        </button>
        <p className="mt-4 opacity-80">Last run: {lastRun || 'Not run yet'}</p>
      </section>

      <SystemWorkspaceDirectory
        treeRepairPlan={
          treeStructuralRepairPlan
        }
        treeLiveOutput={
          liveDomainActions("tree")
        }
      />

      <details className="my-6 rounded-[2rem] border border-slate-700 bg-slate-950/60 p-5 text-white">
        <summary className="cursor-pointer text-xl font-bold">
          Legacy Administration
        </summary>

        <p className="mt-3 text-sm text-slate-300">
          Open this section for migration inventory
          and legacy completion evidence.
        </p>

        <div className="mt-5 text-[#21112f]">
          <LegacyCommandCenterMigrationInventory />

          <LegacyCommandCenterCompletionCertification />
        </div>
      </details>

      <details className="my-6 rounded-[2rem] border border-slate-700 bg-slate-950/60 p-5 text-white">
        <summary className="cursor-pointer text-xl font-bold">
          Legacy Command Center Panels
        </summary>

        <p className="mt-3 text-sm text-slate-300">
          Open this section only when reviewing legacy runtime,
          release-gate, executive, incident, and repair panels.
        </p>

        <div className="mt-5 text-[#21112f]">
          <section className="rounded-[2rem] bg-white p-6 my-6 shadow-sm border border-emerald-200">
            <div className="flex items-start justify-between gap-4">
          <div>
            <p className="tracking-[0.2em] text-xs font-bold text-emerald-700">
              MEMBERSHIP GOVERNOR RELEASE GATE
            </p>
            <h2 className="mt-2 text-3xl font-bold">
              Sponsored Invite Pretest
            </h2>
            <p className="mt-2 text-slate-600">
              Runs the complete reservation and recipient-resolution path with a synthetic recipient. It sends no email, SMS, contact request, or share.
            </p>
          </div>

          <span
            className={
              'rounded-full px-4 py-2 text-sm font-bold ' +
              (sponsoredPretestFresh
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-rose-100 text-rose-800')
            }
          >
            {sponsoredPretestFresh
              ? 'PASS'
              : 'BLOCKED'}
          </span>
        </div>

        <button
          type="button"
          onClick={runSponsoredInvitePretest}
          disabled={sponsoredPretestRunning}
          className="mt-5 w-full rounded-2xl bg-emerald-700 px-6 py-4 font-bold text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
        >
          {sponsoredPretestRunning
            ? 'Running No-Send Pretest...'
            : 'Run Sponsored Invite Pretest'}
        </button>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-bold uppercase text-slate-500">
              Release decision
            </p>
            <p className="mt-1 text-lg font-bold">
              {sponsoredInviteReleaseGate.status}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              {sponsoredInviteReleaseGate.reason}
            </p>
          </div>

          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-bold uppercase text-slate-500">
              Safety
            </p>
            <p className="mt-1 text-sm">
              External delivery attempted:{' '}
              <b>
                {String(
                  sponsoredPretestReport
                    ?.external_delivery_attempted ??
                  false,
                )}
              </b>
            </p>
            <p className="text-sm">
              Cleanup complete:{' '}
              <b>
                {String(
                  sponsoredPretestReport
                    ?.cleanup_complete ??
                  false,
                )}
              </b>
            </p>
            <p className="text-sm">
              Seat count unchanged:{' '}
              <b>
                {String(
                  sponsoredPretestReport
                    ?.seat_count_unchanged ??
                  false,
                )}
              </b>
            </p>
          </div>
        </div>

        {sponsoredPretestReport && (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm">
            <p>
              <b>Reservation:</b>{' '}
              {sponsoredPretestReport.reservation_status}
            </p>
            <p>
              <b>Resolution:</b>{' '}
              {sponsoredPretestReport.resolution_status}
            </p>
            <p>
              <b>Lifecycle:</b>{' '}
              {sponsoredPretestReport.resolved_invite_status}
            </p>
            <p>
              <b>Certified:</b>{' '}
              {sponsoredPretestReport.certified_at}
            </p>
            <p>
              <b>Expires:</b>{' '}
              {sponsoredPretestReport.expires_at}
            </p>
          </div>
        )}

        {sponsoredPretestError && (
          <div className="mt-4 rounded-2xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
            <b>Pretest failed:</b>{' '}
            {sponsoredPretestError}
          </div>
        )}
      </section>

      <Panel
        title="Sponsored Invite Release Gate"
        value={sponsoredInviteReleaseGate}
      />

      <section className="grid grid-cols-2 gap-4 my-6">
        <div className="rounded-2xl bg-white p-5"><p className="text-gray-500">Governor Core Runtime</p><b className="text-5xl">{runtime?.healthReport?.overallHealth ?? truthScore}</b></div>
        <div className="rounded-2xl bg-white p-5"><p className="text-gray-500">Runtime Signals</p><b className="text-5xl">{activeSignals}</b></div>
        <div className="rounded-2xl bg-white p-5"><p className="text-gray-500">Findings</p><b className="text-5xl">{findings.length}</b></div>
        <div className="rounded-2xl bg-white p-5"><p className="text-gray-500">Repair Queue</p><b className="text-5xl">{repairQueue.length}</b></div>
      </section>



        <Panel
          title="Executive Scoreboard"
          value={pick(runtime, ['executiveScoreboard', 'scoreboard'])}
        />

<Panel
          title="Executive AI Brief"
          value={pick(runtime, ['executiveBrief'])}
        />

<section className="rounded-[2rem] bg-[#fff3cf] p-6 mb-6">
        <h2 className="text-3xl mb-4">Governor Reality Gate</h2>

        <Panel
          title="Reality Checks"
          value={pick(runtime, ['realityChecks'])}
        />

        <p><b>Health 100 is not success.</b> Success means live evidence proves known issues are fixed.</p>
      </section>

      <Panel title="Live Runtime Signal Board" value={runtimeSignals.map(([name,value]) => ({ name, status: has(value) ? 'CONNECTED / SURFACED' : 'Waiting for Governor event. Reason: No runtime evidence has been received for this subsystem yet. Expected trigger: A real action must reach the Governor. Next check: If this panel never changes after exercising its feature, inspect that subsystem event pipeline.' }))} />
      <Panel title="Autonomous Repair Governance" value={pick(runtime, ['autonomousRepairGovernance', 'repairGovernance'])} />
      <Panel title="Autonomous Repair Runner" value={pick(runtime, ['autonomousRepairRunner', 'repairRunner'])} />
          <Panel title="Autonomous Operations Output" value={pick(runtime, ['autonomousOperationsOutput'])} />
          <Panel title="Global Repair Queue" value={pick(runtime, ['autoRepairQueueOutput'])} />
      <Panel title="Governor Decisions" value={pick(runtime, ['governanceDecisions', 'decisions', 'autonomousDecisions'])} />
      <Panel title="Mission Control" value={pick(runtime, ['missions', 'mission'])} />
      <Panel title="Goal Engine" value={pick(runtime, ['goals', 'goalSummary'])} />
      <Panel title="Strategy Engine" value={pick(runtime, ['strategies', 'strategySummary'])} />
      <Panel title="Priority Engine" value={pick(runtime, ['prioritySummary', 'priorities'])} />
      <Panel title="Impact Engine" value={pick(runtime, ['impactReports', 'impact'])} />
      <Panel title="Repair Routing" value={pick(runtime, ['repairRoutes', 'repairRouting', 'routes'])} />
      <Panel title="Verification Loop" value={pick(runtime, ['verificationLoops', 'verificationResults', 'verificationLoop'])} />
      <Panel title="Learning / Memory" value={pick(runtime, ['learningInsights', 'learning', 'memoryInsights'])} />
      <Panel title="Self-Healing Command Layer" value={pick(runtime, ['selfHealing', 'selfHealingPlan', 'autonomousRepairRunner'])} />

      <Panel
        title="Calls Runtime Observation Summary"
        value={pick(runtime, [
          'callsRuntimeObservationSummary',
        ])}
      />

      <Panel
        title="Calls Runtime Observation Timeline"
        value={pick(runtime, [
          'callsRuntimeObservations',
        ])}
      />

        <Panel
          title="Calls End-to-End Lifecycle Certification"
          value={certifyCallsEndToEndLifecycle(
            (
              runtime?.liveActions ??
              runtime?.runtimeSignals ??
              []
            ) as any[],
          )}
        />
        <Panel title="Calls Governor Live Output" value={liveDomainActions("call")} />
        <Panel title="Messaging Governor Live Output" value={pick(runtime, ['messagingGovernor', 'domainRuntimeOutputs.messagingGovernor'])} />
        <Panel title="Membership Governor Live Output" value={pick(runtime, ['membershipGovernor', 'domainRuntimeOutputs.membershipGovernor'])} />
        <Panel
          title="Sponsored Seat Runtime Evidence Projection"
          value={sponsoredSeatRuntimeEvidence}
        />
        <Panel
          title="Sponsored Seat Completion Gate"
          value={sponsoredSeatCompletionGate}
        />
        <Panel
          title="Unsponsored Family Runtime Evidence Projection"
          value={unsponsoredFamilyRuntimeEvidence}
        />
        <Panel
          title="Unsponsored Family Completion Gate"
          value={unsponsoredFamilyCompletionGate}
        />
        <Panel
          title="Membership Counter Runtime Evidence Projection"
          value={membershipCounterRuntimeEvidence}
        />
        <Panel
          title="Membership Counter Completion Gate"
          value={membershipCounterCompletionGate}
        />
        <Panel
          title="Canonical Route Runtime Completion Gate"
          value={canonicalRouteRuntimeCompletionGate}
        />
        <Panel
          title="Security Authority Evidence Projection"
          value={securityAuthorityEvidence}
        />
        <Panel
          title="Security Governor Completion Gate"
          value={securityGovernorCompletionGate}
        />
        <Panel title="Media Governor Live Output" value={pick(runtime, ['mediaGovernor', 'domainRuntimeOutputs.mediaGovernor'])} />
        <Panel title="Raw Runtime Spine" value={runtime} />
          <GovernorLiveTimeline runtime={runtime} />
      <GovernorRepairSuggestions runtime={runtime} />
        </div>
      </details>
    </main>
  );
}
